import './Git.css'

import SEO from '../../../../Components/SEO/SEO'
import PageTitle from '../../../../Hooks/PageTitle';

import git_image from '../../../../Media/Blog.Media/Guides.Media/Git.Media/git_image.png'

const Git = () => {
    PageTitle('Git Setup Guide');
    
	return (
		<>
			<SEO 
                title="Complete Git Setup Guide for Absolute Beginners"
                description="Learn Git from scratch! Beginner-friendly guide covering installation and the 12 essential Git commands every developer needs, explained one by one."
                url="https://yassinebazgour.vercel.app/blog/guides/git-setup-complete-beginners"
                keywords="Git tutorial, Git for beginners, Git setup, Git commands, version control, learn Git, Git commands list, GitHub tutorial, Git branching, Git for developers"
            />
			<section className='git-section'>
                <div className='git-head'>
					<span className='git-title'>
                        Complete Git Setup Guide for Absolute Beginners<br/>
                    </span>
					
					<div className='git-timing-data-container'>
						<span className='time-stamp'><i className='fas fa-calendar'></i> July 2026</span> 
						<span className='reading-time'><i className='fas fa-clock'></i> 20-30 min read</span>
					</div>
					
					<div className='git-key-words-container'>
						<span className='git-key-word'>Git</span>
						<span className='git-key-word'>Version Control</span>
						<span className='git-key-word'>GitHub</span>
						<span className='git-key-word'>Beginner Friendly</span>
					</div>
					
					<img src={git_image} className='git-image' alt="Git version control illustration"/>
				</div>
				<div className='git-content'>
					<p>
						Git is the tool developers use to track changes in their code and work together without 
						stepping on each other's toes. It sounds intimidating at first, but almost everything you'll 
						do day to day comes down to a small set of commands. Once you know these, you can handle 
						the vast majority of real projects.
					</p>
					<h2>Installing Git</h2>
					<p>
						On macOS, install Git through Homebrew or the Xcode command line tools. On Windows, download 
						the official Git for Windows installer, which also gives you Git Bash, a terminal that feels 
						like Linux. On Linux, Git is usually available directly through your package manager. Once 
						it's installed, open a terminal and check the version to confirm everything worked.
					</p>
					<pre className='git-command'><code>git --version</code></pre>
					<p>
						This prints the installed Git version. If you see a version number, you're ready to go.
					</p>
					<h2>The 12 Git Commands You'll Actually Use</h2>
					<p>
						Here are the commands that cover almost all of everyday Git work, in the order you'd typically 
						reach for them.
					</p>

					<h3>1. Set up your identity</h3>
					<pre className='git-command'><code>git config --global user.name "Your Name"</code></pre>
					<pre className='git-command'><code>git config --global user.email "you@example.com"</code></pre>
					<p>
						Run these once after installing Git. They attach your name and email to every commit you make, 
						so your work is properly credited.
					</p>

					<h3>2. Start tracking a project</h3>
					<pre className='git-command'><code>git init</code></pre>
					<p>
						Turns the current folder into a Git repository. Run this once inside a new project you want 
						Git to start tracking.
					</p>

					<h3>3. Copy an existing project</h3>
					<pre className='git-command'><code>git clone &lt;repository-url&gt;</code></pre>
					<p>
						Downloads a full copy of a remote repository, including its entire history, onto your machine. 
						This is usually the first command you run when joining an existing project.
					</p>

					<h3>4. Check what's going on</h3>
					<pre className='git-command'><code>git status</code></pre>
					<p>
						Shows which files have been changed, which are staged and ready to commit, and which aren't 
						tracked yet. This is the command you'll run most often, it's your map of what's happening.
					</p>

					<h3>5. Stage your changes</h3>
					<pre className='git-command'><code>git add &lt;file&gt;</code></pre>
					<pre className='git-command'><code>git add .</code></pre>
					<p>
						Marks files as ready to be included in the next commit. Use a specific filename to stage one 
						file, or a dot to stage everything that's changed.
					</p>

					<h3>6. Save a snapshot</h3>
					<pre className='git-command'><code>git commit -m "describe your change"</code></pre>
					<p>
						Saves the staged changes as a permanent point in your project's history, along with a message 
						explaining what changed. Good commit messages make it much easier to understand a project's 
						history later.
					</p>

					<h3>7. See what changed</h3>
					<pre className='git-command'><code>git diff</code></pre>
					<p>
						Shows the exact line-by-line differences between your current files and the last commit. Useful 
						for double-checking what you're about to stage before you commit it.
					</p>

					<h3>8. Look through history</h3>
					<pre className='git-command'><code>git log</code></pre>
					<p>
						Lists past commits, newest first, with their author, date, and message. It's how you review 
						what's happened in a project over time.
					</p>

					<h3>9. Connect to a remote repository</h3>
					<pre className='git-command'><code>git remote -v</code></pre>
					<p>
						Shows which remote repositories your local project is connected to, and the URLs Git will push 
						to or pull from.
					</p>

					<h3>10. Send your commits online</h3>
					<pre className='git-command'><code>git push</code></pre>
					<p>
						Uploads your committed changes to the remote repository, making them visible to anyone else 
						working on the project.
					</p>

					<h3>11. Get the latest changes</h3>
					<pre className='git-command'><code>git pull</code></pre>
					<p>
						Downloads and merges changes from the remote repository into your local project. Run this 
						before you start new work to make sure you're building on the latest version.
					</p>

					<h3>12. Work on something separately</h3>
					<pre className='git-command'><code>git branch &lt;branch-name&gt;</code></pre>
					<pre className='git-command'><code>git checkout &lt;branch-name&gt;</code></pre>
					<p>
						Creates a new branch and switches to it. Branches let you work on a feature or fix without 
						touching the main version of the project until you're ready to merge it back in.
					</p>

					<h3>13. Bring your work back together</h3>
					<pre className='git-command'><code>git merge &lt;branch-name&gt;</code></pre>
					<p>
						Combines the changes from another branch into the branch you're currently on. This is how 
						finished work on a separate branch gets folded back into the main project.
					</p>

					<h2>Troubleshooting Common Issues</h2>
					<p>
						If the terminal doesn't recognize Git after installing, try restarting it first, that alone 
						fixes most cases. If commits fail with an identity error, make sure you've run the config 
						commands from step one. And if you're ever unsure what will happen next, running 
						<pre className='git-command'><code>git status</code></pre> before doing anything else will tell you 
						exactly where things stand.
					</p>
					<h2>Conclusion</h2>
					<p>
						That's the core of Git: track changes with add and commit, sync with push and pull, and branch 
						off with branch and merge when you need to work on something separately. Everything else in 
						Git builds on these same twelve commands.
					</p>
				</div>
            </section>
		</>
	)
}

export default Git